import { PocketRow, PocketSection } from "@/components/ui";
import { Icons } from "@/components/ui/icons";
import { useT } from "@/i18n";
import { framesGithubMessages } from "@/i18n/messages/frames-github";
import { safeWebUrl } from "@/lib/safe-web-url";
import { openExternalUrl } from "@/lib/open-external-url";

export type ActionRun = {
  name: string;
  /** The companion's mapping of GitHub's status and conclusion. */
  status: "pass" | "fail" | "running" | "queued" | "cancelled" | "skipped";
  duration?: string;
  event?: string;
  branch?: string;
  time?: string;
  url?: string;
};
const STATUS_KEY = {
  pass: "runPassed",
  fail: "runFailed",
  running: "runRunning",
  queued: "runQueued",
  cancelled: "runCancelled",
  skipped: "runSkipped",
} as const;

/** Frame content is stored JSON: a value this build does not know reads as cancelled. */
function runStatus(status: string): ActionRun["status"] {
  return Object.prototype.hasOwnProperty.call(STATUS_KEY, status)
    ? (status as ActionRun["status"])
    : "cancelled";
}

export function ActionsSection({ actions }: { actions: ActionRun[] }) {
  const t = useT(framesGithubMessages);
  if (!actions.length) return null;
  return (
    <PocketSection title={t("checks")} testId="github-actions">
      {actions.map((run, index) => {
        const url = safeWebUrl(run.url);
        const status = runStatus(run.status);
        const statusText = t(STATUS_KEY[status]);
        const tone =
          status === "fail"
            ? "error"
            : status === "running" || status === "queued"
              ? "working"
              : undefined;
        return (
          <PocketRow
            key={`${run.name}-${index}`}
            testId={`github-action-link-${index}`}
            title={run.name}
            leading={<Icons.GitBranch className="h-4 w-4" aria-hidden="true" />}
            secondLine={[
              run.duration
                ? t("runAfter", { status: statusText, duration: run.duration })
                : statusText,
              run.time,
            ]
              .filter(Boolean)
              .join(", ")}
            status={tone ? { tone, text: statusText } : undefined}
            onSelect={
              url
                ? () => {
                    void openExternalUrl(url);
                  }
                : undefined
            }
            hoverActions={
              url ? (
                <Icons.ExternalLink className="h-3 w-3" aria-hidden="true" />
              ) : undefined
            }
          />
        );
      })}
    </PocketSection>
  );
}
